import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useMedia from 'common/hooks/useMedia'
import { fInteger } from 'common/utils/formatNumber'
import { labelWithSpecies } from 'common/utils/species'
import { useGetPondDashboard } from 'modules/pond/api/queries'
import { useCurrentCycleInfo, useSpeciesName } from 'modules/pond/api/selector'
import { netRemainingCount } from 'modules/shrimpTransfer/utils/transferEstimate'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@mui/icons-material/Add'
import { Button, Card, Typography, Stack } from '@mui/material'

import ShrimpTransferRecordDialog from '../ShrimpTransferRecordDialog'

interface ShrimpTransfersRecordCardProps {
  children: React.ReactNode
}

const ShrimpTransfersRecordCard: FC<ShrimpTransfersRecordCardProps> = ({ children }) => {
  const { t } = useTranslation('shrimpTransfer')
  const speciesName = useSpeciesName()
  const showRecordBtn = usePermitToActionFeature([CRUDAction.Create], FeatureKey.ShrimpTransfer)
  const [open, setOpen] = useState(false)
  const isMobile = useMedia('sm')

  const cycleInfo = useCurrentCycleInfo()
  const { data: dashboard } = useGetPondDashboard()
  // Net count left to move (stocked - dead - sold), in place of the old item count.
  const remaining = netRemainingCount(dashboard)
  const isCompleted = cycleInfo?.status === 'completed'

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h5">{labelWithSpecies(t('transfer.title'), speciesName)}</Typography>
          {remaining !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {t('transfer.canMoveMore', { remaining: fInteger(remaining) })}
            </Typography>
          )}
        </Stack>
        {showRecordBtn && !isCompleted && (
          <Button variant="contained" onClick={handleOpen} startIcon={<AddIcon />}>
            {labelWithSpecies(t('transfer.records'), speciesName)}
          </Button>
        )}
      </Stack>
      {children}
      <ShrimpTransferRecordDialog open={open} onClose={handleClose} />
    </Card>
  )
}

export default ShrimpTransfersRecordCard
